import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { Grid2, Typography } from "@mui/material";
import { IntegrationsService } from "@services/integrations-service/integrations-service";
import { ProjectIntegrationsMap } from "@services/integrations-service/integrations-service-types";
import { INTEGRATION_INFO } from "@src/constants/integrations-constants";
import { Dialog, useToast } from "@flat-ui";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { DEFAULT_INPUT_FONT_SIZE } from "@styles/common-styles";
import { ProjectEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState } from "react";

interface Props {
  /** Integrations service instance */
  integrationsService: IntegrationsService;

  /** Integration ID */
  integrationId: FaroCoreApiTypes.IntegrationId;

  /** Callback to set the project integrations */
  setProjectIntegrations(
    projectIntegrations: ProjectIntegrationsMap | undefined
  ): void;

  /** Callback while closing the dialog */
  onClose(): void;
}

/** Renders project integration connect dialog */
export function ProjectIntegrationsDisconnectDialog({
  integrationsService,
  integrationId,
  setProjectIntegrations,
  onClose,
}: Props): JSX.Element {
  const { handleErrorWithToast } = useErrorContext();
  const { openToast } = useToast();
  const { trackEvent } = useTrackEvent();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  async function onDisconnectConfirm(): Promise<void> {
    trackEvent({
      name: ProjectEvents.disconnectIntegration,
      props: { integrationId },
    });

    try {
      setIsLoading(true);
      const updatedProjectIntegrations =
        await integrationsService.disconnectProject(integrationId);
      setProjectIntegrations(updatedProjectIntegrations);

      openToast({
        title: `${INTEGRATION_INFO[integrationId].displayName} has been disabled in this project`,
      });
      onClose();
    } catch (error) {
      handleErrorWithToast({
        id: `disableIntegration-${Date.now().toString()}`,
        title: `Failed to disable ${INTEGRATION_INFO[integrationId].displayName}`,
        error,
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Dialog
      open={true}
      onConfirm={() => void onDisconnectConfirm()}
      showSpinner={isLoading}
      confirmText="Disable"
      title={`Disable ${INTEGRATION_INFO[integrationId].displayName}?`}
      onCancel={onClose}
      variant="danger"
    >
      <Grid2
        maxWidth="100%"
        alignItems="center"
        container
        flexDirection="column"
        justifyContent="center"
      >
        <Typography
          sx={{
            fontSize: DEFAULT_INPUT_FONT_SIZE,
            marginBottom: "32px",
          }}
        >
          {`Disabling ${INTEGRATION_INFO[integrationId].displayName} will prevent
          you from using any of its features in this project. You can enable it
          again at any time`}
        </Typography>
      </Grid2>
    </Dialog>
  );
}
